import { z } from 'zod';

/**
 * Zod schema for validating recurring charge metadata
 * Enforces strong typing and prevents invalid data from being stored
 */
export const RecurringChargeMetadataSchema = z.object({
  amount: z.number().int().positive('Amount must be positive'),
  currency: z.string().min(1, 'Currency is required'),
  cardId: z.string().min(1, 'Card ID is required'), // ← Saved card ID in QBO (persistent, reusable)
  customerName: z.string().min(1, 'Customer name is required'),
  customerEmail: z.string().email('Valid email required'),
  
  // Optional pricing fields
  tax: z.number().int().optional(),
  subtotal: z.number().int().optional(),
  quantity: z.number().int().positive('Quantity must be positive').optional(),
  plan: z.string().min(1, 'Plan must not be empty').optional(),
  ourPrice: z.number().positive('Our price must be positive').optional(),
  microsoftPrice: z.number().positive('Microsoft price must be positive').optional(),
  taxPercentage: z.number().positive('Tax percentage must be positive').optional(),
  discountPercentage: z.number().min(0, 'Discount percentage must be non-negative').optional(),
  
  // Optional customer reference (may be stored for reference)
  customerId: z.string().optional(),
  
  // Subscription metadata for recurring billing setup
  subscriptionMetadata: z.object({
    quantity: z.number().int().positive('Quantity must be positive'),
    plan: z.string().min(1, 'Plan is required'),
    billingCycle: z.enum(['monthly', 'annual']),
  }).optional(),
  
  // Optional fields
  invoice_id: z.string().optional(),
  payment_mode: z.enum(['CREDIT_CARD', 'ECHECK']).optional(),
}).strict(); // ← STRONG: No extra fields allowed

/**
 * TypeScript interface derived from Zod schema
 * Provides type safety for metadata throughout the application
 */
export type RecurringChargeMetadata = z.infer<typeof RecurringChargeMetadataSchema>;

/**
 * Input type for creating a new recurring subscription
 */
export interface CreateRecurringSubscriptionInput {
  customerId: string;
  customerName: string;
  customerEmail: string;
  cardId: string; // ← Saved card ID in QBO
  quantity: number;
  plan: string;
  billingCycle: 'monthly' | 'annual'; // ← Supported billing cycles
  realmId: string;
  adminEmail: string;
}

/**
 * Response type for creating a recurring subscription
 */
export interface CreateRecurringSubscriptionResponse {
  success: boolean;
  subscriptionId?: string;
  error?: string;
}
